import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const About = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden min-h-[60vh] flex items-center bg-dark">
        <div className="absolute inset-0 z-0">
          {/* Abstract Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black opacity-90"></div>
          <div className="absolute top-0 left-0 w-1/2 h-full bg-[#FF914D] opacity-10 blur-3xl rounded-full transform -translate-x-1/3"></div>
          <div className="absolute bottom-0 right-0 w-1/2 h-full bg-[#FF914D] opacity-10 blur-3xl rounded-full transform translate-x-1/3"></div>
        </div>

        <div className="container relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="mb-6 text-5xl font-bold md:text-7xl font-display">
              About <span className="text-[#FF914D]">Us</span>
            </h1>
            <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
              We're a passionate team of digital creatives helping small businesses thrive in the social media landscape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="text-[#FF914D] font-bold tracking-wider uppercase text-sm">Who We Are</span>
              <h2 className="mt-2 text-4xl font-bold font-display text-dark">Our Story</h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="prose prose-lg max-w-none text-center text-gray-600 leading-relaxed space-y-6"
            >
              <p>
                Founded in 2025, <span className="font-bold text-dark">Social Crouton</span> was born to help small businesses find their voice in a loud, overwhelming digital world. We saw passionate founders with big ideas but no clear path online — lost in trends, inconsistent posting, or simply going unheard.
              </p>
              <p>
                So, we rolled up our sleeves and got to work. With a mix of creative spark and smart strategy, we set out to make social media feel less transactional and more human. For us, it's not just about numbers — it's about building something real.
              </p>
              <p className="font-medium text-dark text-xl">
                We don't just "manage" your socials — we become part of your team, your story, and your growth journey forward.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-24 bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <span className="text-[#FF914D] font-bold tracking-wider uppercase text-sm">What Drives Us</span>
            <h2 className="mt-2 text-4xl font-bold font-display text-dark">Our Values</h2>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "Creativity",
                description: "Every project gets fresh ideas and bold thinking — because cookie-cutter content is just… stale.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                ),
                color: "bg-blue-500"
              },
              {
                title: "Authenticity",
                description: "We keep it real — honest content, transparent communication, and strategies that build genuine trust.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ),
                color: "bg-green-500"
              },
              {
                title: "Excellence",
                description: "We aim high and deliver higher. From captions to campaigns, quality is non-negotiable.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                ),
                color: "bg-purple-500"
              },
              {
                title: "Growth",
                description: "Social never sleeps, and neither do we (well, almost). We learn, adapt, and keep your brand ahead of trends.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                ),
                color: "bg-[#FF914D]"
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-8 text-center bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 group"
              >
                <div className={`flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-2xl ${value.color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {value.icon}
                </div>
                <h3 className="mb-3 text-xl font-bold text-dark">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#FF914D] to-[#FFB86B]"></div>
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/cubes.png')" }}></div>

        <div className="container relative z-10 text-center text-white">
          <h2 className="mb-6 text-4xl font-bold font-display">Ready to Work With Us?</h2>
          <p className="max-w-2xl mx-auto mb-10 text-xl opacity-90">
            Let's collaborate to elevate your brand's social media presence and connect with your audience in meaningful ways.
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block"
          >
            <Link to="/contact" className="btn bg-white text-[#FF914D] hover:bg-gray-50 px-10 py-4 rounded-full font-bold text-lg shadow-xl">
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default About;